import { Slide } from '../components/SlideComponents/slide.types';
import { isSaveGeneratedLessonsAsFreeEnabled } from '../hooks/useSaveGeneratedLessonsAsFree';
import BackendService from '../services/backendService';
import { Template } from '../services/TemplateService/Template.type';
import { showErrorNotification } from './notification';

// Helpers used by the internal-only "Save generated lessons as free" toggle
// (see `hooks/useSaveGeneratedLessonsAsFree.tsx`). When the toggle is on, we
// stamp every slide of a freshly generated lesson with `isPremiumContent: false`
// so the lesson is treated as freemium content by the premium-content
// detection (`utils/premiumContentDetection.ts`) and by the backend's
// teaching-session counting (which reads `slide.isPremiumContent`).

export function stampSlidesPremiumContent<S extends { isPremiumContent?: boolean }>(
  slides: S[] | null | undefined,
  isPremium: boolean,
): S[] {
  if (!slides) return [];
  return slides.map(slide => ({ ...slide, isPremiumContent: isPremium }));
}

export function stampSlidesAsFree<S extends { isPremiumContent?: boolean }>(
  slides: S[] | null | undefined,
): S[] {
  return stampSlidesPremiumContent(slides, false);
}

// Persist a freshly generated lesson with all premium markers stripped from its
// slides. Returns the saved template (or the original on no-op / failure) so
// callers can chain off the up-to-date version. No-op when the toggle is off
// or the generated template has no slides.
export async function applySaveAsFreeOverrideIfEnabled(
  generated: Template,
  activeTenantId: string,
  isInternalSaveGeneratedLessonsAsFreeFlagEnabled: boolean,
): Promise<Template> {
  if (
    !isSaveGeneratedLessonsAsFreeEnabled(isInternalSaveGeneratedLessonsAsFreeFlagEnabled)
  ) {
    return generated;
  }

  const originalSlides: Slide[] = generated.content?.slides ?? [];
  if (originalSlides.length === 0) return generated;

  const updatedSlides = stampSlidesAsFree(originalSlides);

  try {
    const saved = await BackendService.instance.template.templateAutoSave(
      {
        id: generated.id,
        slides: updatedSlides,
        modifiedAt: Date.now(),
      },
      activeTenantId,
    );
    return saved ?? generated;
  } catch (err) {
    // Don't block the user from seeing their lesson if the override save
    // fails — surface a notification so the operator knows the lesson is still
    // marked premium and they need to retry, then return the original
    // generated template.
    // eslint-disable-next-line no-console
    console.warn('[saveGeneratedLessonsAsFree] override save failed', err);
    showErrorNotification({
      message: 'Could not save generated lesson as free',
      description:
        'The lesson was generated but is still marked as premium. Toggle the setting off and on, or edit the lesson directly to retry.',
    });
    return generated;
  }
}
